/**
 * Step 4a — specials (owner: specials team), ARCHITECTURE §5 "Specials" + "Signature specials". Runs after
 * `rulesPre` (so it sees the snap of this tick) and BEFORE the controllers (so SPECIAL_POSE applies on the
 * activation tick). Never runs during hit-stop (step.ts returns early), so telegraphs and timers freeze with the
 * rest of the sim.
 *
 * Lifecycle (`PlayerSim.special`, `frame` = ticks since entering `phase`, 0 on the entry tick):
 *
 *   IDLE ──Special press, power full, legal (query.ts)──▶ TELEGRAPH (power spent, SPECIAL_POSE, SPECIAL_TELEGRAPH)
 *   IDLE ──Blitz press in PRE_SNAP (defense)──▶ ARMED (secret: no event, no pose, power NOT spent)
 *   ARMED ──the snap (rules set rushSkipped / SNAP{blitz})──▶ TELEGRAPH (power spent now, SPECIAL_TELEGRAPH)
 *   TELEGRAPH ──18 f (Blitz: or RUSH_GO, whichever first)──▶ ACTIVE + SPECIAL_ACTIVATE
 *   ACTIVE ──effect consumed / timed out──▶ SPENT + SPECIAL_END{consumed}
 *   any ──PLAY_END──▶ SPENT (+ SPECIAL_END{consumed:false} if TELEGRAPH/ACTIVE; rules/post.ts)
 *   any ──lineUpForSnap──▶ IDLE (state.ts)
 *
 * Effects per special:
 *  - HAIL_MARY: ACTIVE = armed until the next throw (ball/launch.ts reads it: speed × hailMary.speedMul, flight
 *    gravity × gravityMul, the thrower's catch radius × catchRadiusMul). The tick after the throw launched a
 *    `ball.hailMary` flight by this player: SPENT + SPECIAL_END{consumed:true}.
 *  - BULLDOZER: ACTIVE for `bulldozer.durationFrames` (timer). The contact module consumes it on a grounded tackle
 *    (BULLDOZE, it sets SPENT and emits SPECIAL_END{consumed:true} itself); a timeout here = SPECIAL_END{false}.
 *  - MIRAGE: when the telegraph ends the decoy spawns at the carrier and runs the OPPOSITE way of the carrier's
 *    facing at runSpeed × SPD × mirage.decoySpeedMul, falling with PHYSICS.gravity when spawned airborne. It is
 *    up (decoyActive) at the end of exactly mirage.decoyFrames ticks, the spawn tick included. Non-solid; the contact module pops it (DECOY_POP + WHIFF + FAKE_SUCCESS 'mirage', it
 *    clears `decoyActive`): seen here on the next tick → SPENT + SPECIAL_END{consumed:true}. Timeout →
 *    SPECIAL_END{consumed:false}. `timer` mirrors the decoy frames left (HUD).
 *  - BLITZ: the shortened rush count is the effect (rules/pre.ts). When the telegraph ends: SPECIAL_ACTIVATE then
 *    SPENT + SPECIAL_END{consumed:true} on the same tick.
 *
 * Protocol with the other modules (rules/helpers.ts): specials only SPEND power (never add), never activate in
 * DEAD / CONVERSION / GAME_OVER, and enter the pose with `setState(p, SPECIAL_POSE)` (the controller holds the
 * pose while the phase is TELEGRAPH, at most telegraphFrames). The Special button belongs to this module only.
 * Allocation-free except for events.
 */
import { DT, MOVE, PHYSICS, PLAY_MAX_X, PLAY_MIN_X, POWER, SPECIALS_TUNING, SpecialId } from '@hf/shared';
import { BallState, BtnIndex, Phase, PlayerState, SpecialPhase } from '../enums';
import { setLock, setState } from '../player/fsm';
import { consumePress, hasBufferedPress } from '../player/input';
import type { SimEventSink } from '../sink';
import type { PlayerSim, SimState } from '../state';
import { specialAvailable } from './query';

const T = SPECIALS_TUNING;

const enter = (p: PlayerSim, phase: SpecialPhase): void => {
  p.special.phase = phase;
  p.special.frame = 0;
};

/** Cooldown (handegg-roster.md) started the instant a special enters SPENT. Blitz has none: `usesThisDrive` gates it. */
const cooldownFramesFor = (id: number): number => {
  switch (id) {
    case SpecialId.HAIL_MARY:
      return T.hailMary.cooldownFrames;
    case SpecialId.BULLDOZER:
      return T.bulldozer.cooldownFrames;
    case SpecialId.MIRAGE:
      return T.mirage.cooldownFrames;
    case SpecialId.ROCKET_BOOTS:
      return T.rocketBoots.cooldownFrames;
    case SpecialId.BIG_HEAD:
      return T.bigHead.cooldownFrames;
    case SpecialId.GROUND_POUND:
      return T.groundPound.cooldownFrames;
    case SpecialId.DOUBLE_JUMP:
      return T.doubleJump.cooldownFrames;
    case SpecialId.MAGNET_ONSIDE:
      return T.magnet.cooldownFrames;
    default:
      return 0;
  }
};

const end = (s: SimState, ev: SimEventSink, p: PlayerSim, consumed: boolean): void => {
  const sp = p.special;
  enter(p, SpecialPhase.SPENT);
  sp.timer = 0;
  sp.decoyActive = false;
  sp.decoyFrames = 0;
  if (sp.id === SpecialId.BLITZ) sp.usesThisDrive++;
  else sp.cooldownFrames = cooldownFramesFor(sp.id);
  if (ev)
    ev.push({ type: 'SPECIAL_END', tick: s.tick, actor: p.index, special: sp.id as SpecialId, consumed });
};

/** Spend the power and start the 18 f telegraph (pose + SPECIAL_TELEGRAPH). */
const startTelegraph = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const left = p.power - POWER.max;
  p.power = left > 0 ? left : 0;
  enter(p, SpecialPhase.TELEGRAPH);
  p.special.timer = 0;
  setState(p, PlayerState.SPECIAL_POSE);
  if (ev)
    ev.push({
      type: 'SPECIAL_TELEGRAPH',
      tick: s.tick,
      actor: p.index,
      x: p.x,
      y: p.y,
      special: p.special.id as SpecialId,
    });
};

/** Mirage decoy motion: constant run speed, gravity while airborne, ground at y = headRadius, soft play bounds. */
const moveDecoy = (p: PlayerSim): void => {
  const sp = p.special;
  const r = p.stats.headRadius;
  // Locals merged with Math.min/max only (never `x = CONST` in a branch): keeps every value a raw double, so the
  // optimizer never boxes a HeapNumber per tick.
  let vy = sp.decoyVy;
  if (sp.decoyY > r || vy > 0) vy = Math.max(vy - PHYSICS.gravity * DT, -PHYSICS.maxFallSpeed);
  const y = sp.decoyY + vy * DT;
  if (y <= r) vy = Math.max(vy, 0);
  const x = sp.decoyX + sp.decoyVx * DT;
  sp.decoyY = Math.max(y, r);
  sp.decoyVy = vy;
  sp.decoyX = Math.min(Math.max(x, PLAY_MIN_X), PLAY_MAX_X);
  // a decoy that reached the soft play boundary stops there
  if ((x <= PLAY_MIN_X && sp.decoyVx < 0) || (x >= PLAY_MAX_X && sp.decoyVx > 0)) sp.decoyVx = 0;
};

const spawnDecoy = (p: PlayerSim): void => {
  const sp = p.special;
  const dir = p.facing >= 0 ? -1 : 1;
  sp.decoyActive = true;
  sp.decoyX = p.x;
  sp.decoyY = p.y;
  sp.decoyFacing = dir;
  sp.decoyVx = dir * MOVE.runSpeed * p.stats.speedMul * T.mirage.decoySpeedMul;
  sp.decoyVy = p.grounded ? 0 : p.vy;
  sp.decoyFrames = T.mirage.decoyFrames;
  sp.timer = sp.decoyFrames;
  moveDecoy(p); // the spawn tick is the decoy's first frame (it moves with the carrier this tick)
};

/** Telegraph over: the effect starts. */
const activate = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const sp = p.special;
  enter(p, SpecialPhase.ACTIVE);
  sp.timer = 0;
  if (sp.id === SpecialId.BULLDOZER) sp.timer = T.bulldozer.durationFrames;
  else if (sp.id === SpecialId.MIRAGE) spawnDecoy(p);
  else if (sp.id === SpecialId.ROCKET_BOOTS) sp.timer = T.rocketBoots.durationFrames;
  else if (sp.id === SpecialId.BIG_HEAD) sp.timer = T.bigHead.durationFrames;
  else if (sp.id === SpecialId.DOUBLE_JUMP) sp.timer = T.doubleJump.durationFrames;
  else if (sp.id === SpecialId.MAGNET_ONSIDE) sp.timer = T.magnet.durationFrames;
  // x/y = the activator (for Mirage: the decoy's spawn point, i.e. the carrier, so VFX cannot tell them apart).
  if (ev)
    ev.push({
      type: 'SPECIAL_ACTIVATE',
      tick: s.tick,
      actor: p.index,
      x: p.x,
      y: p.y,
      special: sp.id as SpecialId,
    });
  // Blitz: the shortened rush count WAS the effect.
  if (sp.id === SpecialId.BLITZ) end(s, ev, p, true);
  // Ground Pound: an instant shockwave — a grounded opponent holding the ball within radius is stunned, now.
  else if (sp.id === SpecialId.GROUND_POUND) {
    const opp = s.players[p.index === 0 ? 1 : 0];
    const b = s.ball;
    const carrying = b.state === BallState.HELD && b.holder === opp.index;
    const dx = opp.x - p.x;
    const dy = opp.y - p.y;
    const hit = carrying && opp.grounded && Math.sqrt(dx * dx + dy * dy) <= T.groundPound.radius;
    if (hit) {
      setLock(opp, PlayerState.STUNNED, T.groundPound.freezeFrames);
      if (ev) ev.push({ type: 'STUN', tick: s.tick, actor: opp.index, x: opp.x, y: opp.y, frames: T.groundPound.freezeFrames });
    }
    end(s, ev, p, hit);
  }
};

/** ACTIVE effects: consumption checks and timers. */
const tickActive = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const sp = p.special;
  switch (sp.id) {
    case SpecialId.HAIL_MARY:
      // The throw (controller → ball/launch.ts) happened after this module last ran.
      if (s.ball.hailMary && s.ball.thrownBy === p.index) end(s, ev, p, true);
      return;
    case SpecialId.BULLDOZER:
      if (sp.timer > 0) sp.timer--;
      if (sp.timer <= 0) end(s, ev, p, false);
      return;
    case SpecialId.MIRAGE:
      if (!sp.decoyActive) {
        end(s, ev, p, true); // popped by a lunge (contact/resolve.ts): it did its job
        return;
      }
      sp.decoyFrames--;
      if (sp.decoyFrames <= 0) {
        end(s, ev, p, false); // ran its decoyFrames: vanishes
        return;
      }
      sp.timer = sp.decoyFrames;
      moveDecoy(p);
      return;
    case SpecialId.ROCKET_BOOTS:
    case SpecialId.BIG_HEAD:
      // Passive buffs: running their full course is them doing their job.
      if (sp.timer > 0) sp.timer--;
      if (sp.timer <= 0) end(s, ev, p, true);
      return;
    case SpecialId.DOUBLE_JUMP:
      // The extra jump itself is spent from the controller (player/controller.ts `consumeDoubleJump`); a timeout
      // here means it went unused this play.
      if (sp.timer > 0) sp.timer--;
      if (sp.timer <= 0) end(s, ev, p, false);
      return;
    case SpecialId.MAGNET_ONSIDE: {
      const b = s.ball;
      if (b.state === BallState.HELD && b.holder === p.index) {
        end(s, ev, p, true); // pulled it home
        return;
      }
      if (b.state === BallState.AIR || b.state === BallState.LOOSE) {
        const dx = p.x - b.x;
        const dy = p.y - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 1e-6 && dist <= T.magnet.radius) {
          const pull = T.magnet.pullAccel * DT;
          b.vx += (dx / dist) * pull;
          b.vy += (dy / dist) * pull;
        }
      }
      if (sp.timer > 0) sp.timer--;
      if (sp.timer <= 0) end(s, ev, p, false);
      return;
    }
    default:
      end(s, ev, p, false);
  }
};

/** Double Jump's second hop was taken (player/controller.ts, while AIRBORNE): spend the special, now. */
export const consumeDoubleJump = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  if (p.special.id === SpecialId.DOUBLE_JUMP && p.special.phase === SpecialPhase.ACTIVE) end(s, ev, p, true);
};

const updatePlayer = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const sp = p.special;
  if (sp.cooldownFrames > 0) sp.cooldownFrames--;
  const ph = s.rules.phase;
  const live = ph === Phase.RUSH_COUNT || ph === Phase.LIVE;
  switch (sp.phase) {
    case SpecialPhase.ARMED:
      // Revealed at the snap (the rules already shortened the rush count and emitted SNAP{blitz:true}).
      if (live) startTelegraph(s, ev, p);
      return;
    case SpecialPhase.TELEGRAPH:
      if (!live) return; // the whistle ends it (rules/post.ts); never activate in DEAD / CONVERSION / GAME_OVER
      sp.frame++;
      if (sp.frame >= T.telegraphFrames || (sp.id === SpecialId.BLITZ && ph === Phase.LIVE))
        activate(s, ev, p);
      return;
    case SpecialPhase.ACTIVE:
      if (!live) return;
      sp.frame++;
      tickActive(s, ev, p);
      return;
    case SpecialPhase.SPENT:
      sp.frame++;
      return;
    default:
  }
  // IDLE: a buffered Special press activates when legal; otherwise it waits in the buffer (and expires).
  if (!hasBufferedPress(p.input, BtnIndex.S) || !specialAvailable(s, p.index)) return;
  consumePress(p.input, BtnIndex.S);
  if (sp.id === SpecialId.BLITZ) {
    enter(p, SpecialPhase.ARMED); // hidden: no event, no pose, power spent at the snap
    sp.timer = 0;
    return;
  }
  startTelegraph(s, ev, p);
};

export const updateSpecials = (s: SimState, ev: SimEventSink): void => {
  updatePlayer(s, ev, s.players[0]);
  updatePlayer(s, ev, s.players[1]);
};
